import { memo, useCallback } from 'react'
import { HeartIcon, CheckCircleIcon, CameraIcon, ShareIcon, MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import { createLogger } from '../../../utils/logger'
import { toast } from '../../../services/toastService'
import { useTranslation } from 'react-i18next'
import { channelsAPI } from '../../../services/api'
import { sightingAPI } from '../../../services/api'
import { usePushSubscription } from '../../../hooks/usePushSubscription'
import { useAsyncOperation } from '../../../hooks/useAsyncOperation'

const logger = createLogger('EntityPopupActions')

/**
 * EntityPopupActions - Action buttons for entity popup
 * 
 * Features:
 * - View details button (normal mode)
 * - Confirm sighting button (entity mode)
 * - Total sightings count
 * - Disabled state for own sightings
 * 
 * @param {Object} props
 * @param {string} props.variant - 'normal' | 'entity'
 * @param {Object} props.entity - Entity data
 * @param {boolean} props.isOwnSighting - User owns this sighting
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onConfirm - Confirm button handler
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render action buttons
 */
const EntityPopupActions = memo(function EntityPopupActions({
  variant,
  entity,
  isOwnSighting,
  onNavigate,
  onConfirm,
  user,
  isFavorite,
  toggleFavorite,
  isTogglingFavorite,
  entityId,
  isImmersiveMode = false,
  onTabChange,
  navigate,
  onClosePopup,
  sightingId,
  sightingUserId,
  onSpotEntity,
  onBook = null,
}) {
  const { t } = useTranslation()
  const { isSubscribed, subscribe } = usePushSubscription()
  const { loading: isStillHereLoading, execute: executeStillHere } = useAsyncOperation()

  // For normal-mode popup: compute ownership from sightingUserId (API: currentSightingUserId)
  const isOwnNormalSighting = !!(user && sightingUserId && user.id === sightingUserId)

  // "Still Here" — creates a fresh sighting at the entity's current location.
  // Used when the logged-in user owns the current sighting and cannot self-confirm.
  const handleStillHere = useCallback((e) => {
    e.stopPropagation()
    if (!user) {
      if (onClosePopup) onClosePopup()
      if (isImmersiveMode && onTabChange) onTabChange('auth')
      else if (navigate) navigate('/login', { state: { from: window.location.pathname } })
      return
    }
    const targetEntityId = entity.entityId || entity.entity_id
    const lat = entity.currentLatitude
    const lng = entity.currentLongitude
    if (!targetEntityId || !lat || !lng) return
    executeStillHere(
      () => sightingAPI.createSighting({ entityId: targetEntityId, latitude: lat, longitude: lng, sightingType: 'tag' }, { silentError: true }),
      {
        silent: true,
        onSuccess: () => toast.success(t('sightings.stillHereSuccess') || 'Location updated! Thanks for keeping the map fresh.'),
        onError: (err) => toast.error(err?.response?.data?.error || t('toast.error.generic') || 'Something went wrong'),
      }
    )
  }, [user, entity, executeStillHere, onClosePopup, isImmersiveMode, onTabChange, navigate, t])

  const handleToggleFavorite = useCallback(async (e) => {
    e.stopPropagation()    
    if (!user) {
      // Close popup before opening signup panel to prevent map resize issues
      if (onClosePopup) {
        onClosePopup()
      }
      
      if (isImmersiveMode && onTabChange) {
        onTabChange('auth')
      } else if (navigate) {
        navigate('/register', { state: { from: window.location.pathname } })
      } else {
        toast.info(t('auth.register.title'))
      }
      return
    }
    
    // Check if already favorited before toggling
    const wasFavorite = isFavorite
    const result = await toggleFavorite(entityId)
    
    // If just favorited (not unfavorited) and we got a channelId, navigate to contact tab
    // This matches the behavior from EntityDisplayPanel which works in production
    if (!wasFavorite && result?.data?.channelId && isImmersiveMode && onTabChange) {
      const channelId = result.data.channelId

      // Ask for push permission once after first favorite (non-blocking)
      if (!isSubscribed) {
        subscribe()
      }
      
      // Close popup before opening panel to prevent map resize issues
      if (onClosePopup) {
        onClosePopup()
      }
      
      // Backend invalidates cache, wait briefly for state updates
      setTimeout(() => {
        // Call onTabChange directly with all 3 parameters like EntityDisplayPanel does
        onTabChange('contact', null, channelId)
        toast.success(t('favorites.channelJoined') || 'You\'ve joined the channel! Check the Contact tab to connect with the community.')
      }, 500)
    }
  }, [user, toggleFavorite, entityId, t, isImmersiveMode, onTabChange, navigate, isFavorite, onClosePopup])

  // "Spot it here" — opens SpotMode pre-filled with this entity so the user
  // can drop their own pin at the correct current location.
  const handleSpotItHere = useCallback((e) => {
    e.stopPropagation()
    if (!user) {
      if (onClosePopup) onClosePopup()
      if (isImmersiveMode && onTabChange) {
        onTabChange('auth')
      } else if (navigate) {
        navigate('/login', { state: { from: window.location.pathname } })
      }
      return
    }
    if (onClosePopup) onClosePopup()
    if (onSpotEntity) onSpotEntity(entity)
  }, [user, onClosePopup, isImmersiveMode, onTabChange, navigate, onSpotEntity, entity])

  const handleViewDetails = useCallback((e) => {    e.stopPropagation()
    const targetId = entity.entityId || entity.id || entity.entity_id || (entity.entity && entity.entity.id)
    if (!targetId) {
      logger.error('Missing entity id for details navigation', { entity })
      toast.error(t('toast.error.detailsUnavailable'))
      return
    }
    onNavigate(targetId)
  }, [entity, onNavigate])

  const handleShare = useCallback((e) => {
    e.stopPropagation()
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin
    const targetId = entity.entityId || entity.id || entity.entity_id || (entity.entity && entity.entity.id)
    const url = sightingId
      ? `${siteUrl}/sightings/${sightingId}`
      : targetId ? `${siteUrl}/entity/${targetId}` : window.location.href
    if (navigator.share) {
      navigator.share({ url, title: entity.name || 'TrckIt Sighting' }).catch(() => {})
    } else {
      navigator.clipboard.writeText(url).then(
        () => toast.success(t('share.copied', 'Link copied!')),
        () => toast.error(t('share.failed', 'Could not copy link'))
      )
    }
  }, [entity, sightingId, t])

  if (variant === 'normal') {
    return (
        <div className="flex flex-col gap-1.5 pt-2.5 border-t border-gray-200/50">
        {sightingId && (
          isOwnNormalSighting ? (
            <button
              onClick={handleStillHere}
              onTouchEnd={(e) => { e.preventDefault(); handleStillHere(e) }}
              disabled={isStillHereLoading}
              className="w-full px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('sightings.stillHereTitle') || 'Submit a fresh sighting at this location'}
            >
              <MapPinIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
              <span className="pointer-events-none">
                {isStillHereLoading ? (t('map.spotting') || 'Updating…') : (t('sightings.stillHere') || 'Still Here')}
              </span>
            </button>
          ) : (
            <button
              onClick={handleSpotItHere}
              onTouchEnd={(e) => { e.preventDefault(); handleSpotItHere(e) }}
              className="w-full px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs border border-primary/50 bg-primary/5 text-gray-700 hover:bg-primary/15 hover:border-primary"
              title={t('sightings.spotItHereTitle') || 'Spot this entity at your current location'}
            >
              <MapPinIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
              <span className="pointer-events-none">
                {user ? (t('sightings.spotItHere') || 'Spot it here') : (t('sightings.spotItHereLogin') || 'Log in to Spot')}
              </span>
            </button>
          )
        )}
        {onBook && (
          <button
            onClick={(e) => { e.stopPropagation(); onBook() }}
            style={{ touchAction: 'manipulation' }}
            className="w-full px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs bg-accent text-white hover:opacity-90"
            title={t('bookings.request', 'Book a Service')}
          >
            <CalendarDaysIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
            <span className="pointer-events-none">
              {user ? (t('bookings.request', 'Book a Service')) : (t('bookings.loginToBook', 'Log in to Book'))}
            </span>
          </button>
        )}
        <div className="flex gap-2">
          <button
            onClick={handleToggleFavorite}
            disabled={isTogglingFavorite}
            className="px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isFavorite ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}
          >
            {isFavorite ? (
              <HeartIconSolid className="w-4 h-4 text-red-500" />
            ) : (
              <HeartIcon className="w-4 h-4 text-gray-600" />
            )}
          </button>
          <button
            onClick={handleShare}
            onTouchEnd={handleShare}
            className="px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50"
            title={t('share.link', 'Share link')}
          >
            <ShareIcon className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={handleViewDetails}
            className="flex-1 px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs bg-primary hover:bg-primary-600 text-white"
            title={entity.totalSightings > 1 ? `View all ${entity.totalSightings} sightings` : 'View Details'}
          >
            <span>{entity.totalSightings > 1 ? `${entity.totalSightings} Sightings` : 'View Details'}</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  // Entity mode - show confirm button (+ Book if entity has bookable services)
  return (
    <div className="pt-2.5 border-t border-gray-200/50 flex flex-col gap-1.5">
      {onBook && (
        <button
          onClick={(e) => { e.stopPropagation(); onBook() }}
          style={{ touchAction: 'manipulation' }}
          className="w-full px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1.5 text-xs bg-accent text-white hover:opacity-90"
          title={t('bookings.request', 'Book a Service')}
        >
          <CalendarDaysIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
          <span className="pointer-events-none">{t('bookings.request', 'Book a Service')}</span>
        </button>
      )}
      <button
        onClick={onConfirm}
        onTouchEnd={onConfirm}
        disabled={isOwnSighting}
        className="w-full px-3 py-1.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-1.5 text-xs bg-primary hover:bg-primary-600 text-white shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary disabled:bg-gray-300 disabled:cursor-not-allowed pointer-events-auto"
        title={isOwnSighting ? t('toast.info.cannotConfirmOwn') : t('confirm.withPhotoTitle') || 'Confirm with photo for higher trust score'}
      >
        <CameraIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
        <span className="pointer-events-none">{t('confirm.withPhoto') || 'Confirm with Photo'}</span>
      </button>
      <button
        onClick={handleShare}
        onTouchEnd={handleShare}
        className="w-full px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 text-xs border border-gray-300 hover:bg-gray-50"
        title={t('share.link', 'Share this sighting')}
      >
        <ShareIcon className="w-4 h-4 pointer-events-none flex-shrink-0" />
        <span className="pointer-events-none">{t('share.sighting', 'Share Sighting')}</span>
      </button>
    </div>
  )
})

export default EntityPopupActions
